import React, { useEffect, useState, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import _ from "lodash";
import { Typography, Box } from "@mui/material";
import {
  isQrCode,
  displayPriceFormat,
  getOrderTypeLabel,
  getColors,
} from "../../utils";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import { showCartModal, showAddressModal } from "../../redux/actions";
import { DrawerWithEmptyCart, Button } from "@dishopsaas/design-system";
import { PRIMARY, ORDER_TYPE_ICONS } from "../../constants";
import { verifyPendingOrderValidity } from "../../Pages/Home/Home.services";
import { getAddress } from "../../../src/components/InfoBar/InfoBar.services";
import styles from "./CartModal.styles";
import {
  analyticsEvents,
  PAGES,
  PROVENANCE,
} from "../../utils/firebaseAnalytics";
import { useNavigate, useLocation } from "react-router-dom";
import CartItemsList from "./CartItemsList";
import { useSignInSignUp } from "../../hooks/useSignInSignUp";
import { navigateWithQr } from "../../utils/routing.utils";
import { cartDescription } from "../../constants/text";

export default function CartModal() {
  const { handleOpenLogin } = useSignInSignUp();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const {
    userReducer: { userConnected },
    componentReducer: { cartModal },
    pendingOrderReducer,
  } = useAppSelector((state) => state);

  const { commande, totalProductPriceOrder, orderType } = pendingOrderReducer;

  const totalArticles = useMemo(
    () => Object.values(commande || {}).reduce((sum, item) => sum + item.nb, 0),
    [commande],
  );

  const openAddress = () => dispatch(showAddressModal(true));

  const proceedToPaymentSummary = () => {
    if (userConnected || isQrCode()) {
      navigateWithQr(navigate, location.pathname, "/payment-summary");
      dispatch(showCartModal(false));
    } else {
      handleOpenLogin();
    }
  };

  useEffect(() => {
    if (!cartModal) return;

    const init = async () => {
      setLoading(true);
      try {
        await verifyPendingOrderValidity();
        analyticsEvents.logPageViewEvent(PAGES.CART, PROVENANCE.MENU_PAGE);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [cartModal]);

  const renderAlternativeComponent = () => {
    const orderTypeLabel = getOrderTypeLabel(orderType);
    const orderTypeIcon = ORDER_TYPE_ICONS[orderType];
    const addressLabel = getAddress();
    const { bgwhite, border } = getColors();

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          width: "100%",
          bgcolor: bgwhite,
        }}
      >
        <Typography variant="h5" sx={styles.titleHeader}>
          Panier
        </Typography>

        <Typography variant="body1" sx={styles.subtitleHeader}>
          {`${totalArticles} Article${totalArticles > 1 ? "s" : ""}`}
        </Typography>

        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            pt: 2,
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Button
            label={orderTypeLabel}
            leftIcon={orderTypeIcon}
            colorType="transparent"
            size="small"
            borderRadius={32}
            rightIcon="ChevronDown"
            withoutHover
            onClick={openAddress}
            labelSx={styles.labelSx}
          />

          <Button
            label={addressLabel}
            leftIcon="position"
            colorType="transparent"
            size="small"
            borderRadius={32}
            rightIcon="ChevronDown"
            withoutHover
            onClick={openAddress}
            labelSx={styles.labelSx}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            maxWidth: "480px",
            overflowY: "auto",
            mt: 2,
            pb: 8,
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
          }}
        >
          <CartItemsList showCategoryName={false} loading={loading} />
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            bgcolor: bgwhite,
            p: 4,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            borderTop: border,
            borderTopColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5">SOUS-TOTAL</Typography>

            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
              }}
            >
              {displayPriceFormat(totalProductPriceOrder)}
            </Typography>
          </Box>

          <Button
            loading={loading}
            colorType={PRIMARY}
            sx={{ width: "100%" }}
            label={`COMMANDER • ${orderTypeLabel}`}
            onClick={proceedToPaymentSummary}
          />
        </Box>
      </Box>
    );
  };

  const emptyCart = _.isEmpty(commande);
  const { bgwhite } = getColors();

  return (
    <DrawerWithEmptyCart
      open={cartModal}
      size="lg"
      isEmpty={emptyCart}
      description={cartDescription}
      onClose={() => dispatch(showCartModal(false))}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: emptyCart ? "center" : "flex-start",
        pt: { xs: 2, md: 4 },
        pb: 0,
        px: 0,
        bgcolor: bgwhite,
        borderLeft: getColors().border,
        width: emptyCart
          ? 300
          : {
              xs: "auto",
              sm: "auto",
              md: "auto",
              lg: "auto",
            },
        overflow: "hidden",
      }}
      alternativeComponent={renderAlternativeComponent()}
    />
  );
}
